import { useEffect, useMemo, useState } from 'react';
import { ApiError, get, post } from '../api';
import type { Propuesta } from '../tipos';
import { Accion, Avisos, Dist, Vacio } from '../components/ui';
import { etiquetaFecha, formatear } from '../components/GraficoPlan';

interface Resultado {
  recommendationId: string;
  status: 'sent' | 'failed' | 'duplicate';
  docNum: string | null;
  message: string;
}

/**
 * Qué hay que hacer.
 *
 * Es la pantalla que más se mira, así que lo que decide el orden es la
 * urgencia real: primero lo que ya nace tarde, después lo que hay que lanzar
 * antes. Ordenar por código de artículo sería más cómodo de programar y
 * dejaría lo urgente en la página cuatro.
 */
export function Propuestas({
  runId,
  puedeConfirmar,
  esSimulacion,
  onVerArticulo,
}: {
  runId: string;
  puedeConfirmar: boolean;
  esSimulacion: boolean;
  onVerArticulo: (codigo: string, almacen: string) => void;
}): JSX.Element {
  const [filas, setFilas] = useState<Propuesta[]>([]);
  const [total, setTotal] = useState(0);
  const [accion, setAccion] = useState('');
  const [soloTarde, setSoloTarde] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const [cargando, setCargando] = useState(true);
  const [resultados, setResultados] = useState<Resultado[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    const params = new URLSearchParams();
    if (accion) params.set('action', accion);
    if (soloTarde) params.set('late', 'true');
    if (busqueda.trim()) params.set('search', busqueda.trim());

    const t = setTimeout(() => {
      void get<{ recommendations: Propuesta[]; total: number }>(`/runs/${runId}/recommendations?${params}`)
        .then((r) => {
          if (!vigente) return;
          setFilas(r.recommendations);
          setTotal(r.total);
        })
        .catch((e: unknown) => vigente && setError(e instanceof ApiError ? e.message : String(e)))
        .finally(() => vigente && setCargando(false));
    }, 180);

    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [runId, accion, soloTarde, busqueda]);

  const confirmables = useMemo(
    () => filas.filter((f) => f.confirm_status !== 'sent' && f.action !== 'cancel'),
    [filas],
  );

  async function confirmar(): Promise<void> {
    setError(null);
    try {
      const r = await post<{ results: Resultado[] }>(`/runs/${runId}/confirm`, { ids: [...elegidas] });
      setResultados(r.results);
      setElegidas(new Set());
      // Se recargan las filas: las confirmadas tienen que dejar de ofrecerse.
      const params = new URLSearchParams();
      if (accion) params.set('action', accion);
      const nuevo = await get<{ recommendations: Propuesta[]; total: number }>(
        `/runs/${runId}/recommendations?${params}`,
      );
      setFilas(nuevo.recommendations);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}${e.hint ? ` — ${e.hint}` : ''}` : String(e));
    }
  }

  return (
    <div>
      <div className="filtros">
        <input
          placeholder="Buscar artículo…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ minWidth: 200 }}
        />
        <select value={accion} onChange={(e) => setAccion(e.target.value)}>
          <option value="">Todas las acciones</option>
          <option value="purchase">Comprar</option>
          <option value="produce">Fabricar</option>
          <option value="expedite">Adelantar</option>
          <option value="defer">Retrasar</option>
        </select>
        <button className="secundario" aria-pressed={soloTarde} onClick={() => setSoloTarde((v) => !v)}>
          Solo las que llegan tarde
        </button>
        <span className="sutil">{total} propuesta(s)</span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {elegidas.size > 0 && <span className="sutil">{elegidas.size} elegida(s)</span>}
          <button
            className="primario"
            disabled={!puedeConfirmar || esSimulacion || elegidas.size === 0}
            onClick={() => void confirmar()}
            title={
              esSimulacion
                ? 'Este plan es una simulación: sus propuestas no crean documentos en SAP'
                : !puedeConfirmar
                  ? 'Tu perfil puede consultar el plan pero no confirmarlo'
                  : 'Crea los documentos en SAP'
            }
          >
            Confirmar en SAP
          </button>
        </div>
      </div>

      {error ? <Avisos titulo="No se pudo confirmar" items={[error]} error /> : null}

      {resultados ? (
        <div className="aviso-caja" style={{ borderColor: 'var(--bien)' }}>
          <div className="titulo">Resultado de la confirmación</div>
          <ul>
            {resultados.map((r) => (
              <li key={r.recommendationId}>
                {r.status === 'sent' ? '✓ ' : r.status === 'duplicate' ? '= ' : '× '}
                {r.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="tarjeta" style={{ padding: 0 }}>
        <div className="tabla-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  <input
                    type="checkbox"
                    aria-label="Elegir todas"
                    checked={elegidas.size > 0 && elegidas.size === confirmables.length}
                    onChange={(e) =>
                      setElegidas(e.target.checked ? new Set(confirmables.map((f) => f.id)) : new Set())
                    }
                  />
                </th>
                <th>Artículo</th>
                <th>Acción</th>
                <th className="num">Cantidad</th>
                <th>Necesita</th>
                <th>Lanzar</th>
                <th>Estado</th>
                <th>Para cubrir</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Elegir ${f.item_code}`}
                      disabled={f.confirm_status === 'sent'}
                      checked={elegidas.has(f.id)}
                      onChange={(e) => {
                        const s = new Set(elegidas);
                        if (e.target.checked) s.add(f.id);
                        else s.delete(f.id);
                        setElegidas(s);
                      }}
                    />
                  </td>
                  <td>
                    <button
                      className="nav-link"
                      onClick={() => onVerArticulo(f.item_code, f.warehouse)}
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        font: 'inherit',
                        color: 'var(--serie-1)',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <strong>{f.item_code}</strong>
                    </button>
                    <div className="sutil">
                      {f.item_name} · almacén {f.warehouse}
                    </div>
                  </td>
                  <td>
                    <Accion accion={f.action} />
                    {f.note ? <div className="sutil">{f.note}</div> : null}
                  </td>
                  <td className="num mono">{formatear(f.quantity)}</td>
                  <td className="mono">{etiquetaFecha(f.due_date)}</td>
                  <td className="mono">
                    {etiquetaFecha(f.release_date)}
                    {f.late_days > 0 ? (
                      <div>
                        <Dist tono="critico" icono="■">{f.late_days} d tarde</Dist>
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {f.confirm_status === 'sent' ? (
                      <Dist tono="bien" icono="✓">En SAP {f.confirm_doc ?? ''}</Dist>
                    ) : f.confirm_status === 'failed' ? (
                      <Dist tono="critico" icono="×">Falló</Dist>
                    ) : f.action === 'purchase' && !f.vendor ? (
                      <Dist tono="aviso" icono="▲">Sin proveedor</Dist>
                    ) : (
                      <span className="sutil">Pendiente</span>
                    )}
                  </td>
                  <td>
                    {f.pegging.length === 0 ? (
                      <span className="sutil">—</span>
                    ) : (
                      <div className="sutil">
                        {f.pegging.slice(0, 2).map((p, i) => (
                          <div key={i}>
                            {formatear(p.quantity)} · {p.label}
                          </div>
                        ))}
                        {f.pegging.length > 2 ? <div>y {f.pegging.length - 2} más</div> : null}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cargando ? <Vacio>Cargando…</Vacio> : filas.length === 0 ? <Vacio>No hay ninguna propuesta con estos filtros.</Vacio> : null}
      </div>
    </div>
  );
}
